import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, fmtINR, fmtPct, fmtDate } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Warning, DownloadSimple, Check, X, Receipt, TrendUp, TrendDown, CaretRight, Storefront, FilePdf, Eye, ArrowsClockwise, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PaymentApprovalsContent from "@/components/PaymentApprovalsContent";

const STORE_COLORS = {
  willingdon: "bg-indigo-50 text-indigo-700 border-indigo-200",
  bandra: "bg-teal-50 text-teal-700 border-teal-200",
  powai: "bg-amber-50 text-amber-800 border-amber-200",
};
const FALLBACK_STORE_COLOR = "bg-slate-100 text-slate-700 border-slate-200";

function ReconApprovalsContent({ store, refreshKey }) {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/approvals", { params: { store } });
      const data = res.data || [];
      setGroups(data);
      setSelected(new Set());
      // Default: everything starts collapsed; user expands on click.
      setExpanded(new Set());
    } catch { toast.error("Failed to load approvals"); }
    finally { setLoading(false); }
  }, [store]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const allItemIds = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.id)), [groups]);

  const toggleItem = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleVendor = (group) => {
    const ids = group.items.map((i) => i.id);
    const allSelected = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === allItemIds.length) setSelected(new Set());
    else setSelected(new Set(allItemIds));
  };
  const toggleExpand = (vendor) => {
    const next = new Set(expanded);
    next.has(vendor) ? next.delete(vendor) : next.add(vendor);
    setExpanded(next);
  };
  const expandAll = () => setExpanded(new Set(groups.map((g) => g.vendor)));
  const collapseAll = () => setExpanded(new Set());

  const action = async (act) => {
    if (selected.size === 0) { toast.error("Select at least one row"); return; }
    try {
      await api.post("/approvals/action", { ids: Array.from(selected), action: act });
      toast.success(`${selected.size} row(s) ${act === "approve" ? "approved" : "rejected"}`);
      load();
    } catch { toast.error("Action failed"); }
  };

  const exportCSV = async () => {
    try {
      const ids = Array.from(selected);
      const res = await api.post("/approvals/export", { ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `approvals_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch { toast.error("Export failed"); }
  };

  const flaggedVendors = groups.filter((g) => g.flagged).length;
  const totalLineItems = groups.reduce((s, g) => s + g.item_count, 0);
  const totalAmount = groups.reduce((s, g) => s + g.total_amount, 0);
  const visibleGroups = onlyFlagged ? groups.filter((g) => g.flagged) : groups;

  return (
    <div data-testid="approvals-tab" className="space-y-5">
      {/* Section header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-violet-100 text-violet-700"><Receipt size={15} weight="duotone" /></div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Payment approvals</h2>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Chip label="Vendors" value={groups.length} color="bg-slate-100 text-slate-700 border-slate-200" />
            <Chip label="Line items" value={totalLineItems} color="bg-slate-100 text-slate-700 border-slate-200" />
            <Chip
              label={onlyFlagged ? "Showing flagged only" : "Flagged vendors"}
              value={flaggedVendors}
              color={`chip-filter ${onlyFlagged ? "is-active" : ""} bg-rose-100 text-rose-700 border-rose-200`}
              onClick={() => setOnlyFlagged((v) => !v)}
              testId="flagged-vendors-chip"
            />
            <Chip label="Total value" value={fmtINR(totalAmount)} color="bg-indigo-100 text-indigo-700 border-indigo-200" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={expandAll} className="h-9 rounded-sm text-slate-600" data-testid="expand-all-btn">Expand all</Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="h-9 rounded-sm text-slate-600" data-testid="collapse-all-btn">Collapse all</Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="h-9 rounded-sm border-slate-300 hover:bg-slate-50" data-testid="export-csv-btn">
            <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => action("reject")} className="h-9 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800" data-testid="reject-btn">
            <X size={14} weight="bold" className="mr-1.5" /> Reject
          </Button>
          <Button size="sm" onClick={() => action("approve")} className="h-9 rounded-sm bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-sm" data-testid="approve-btn">
            <Check size={14} weight="bold" className="mr-1.5" /> Approve {selected.size > 0 && `(${selected.size})`}
          </Button>
        </div>
      </div>

      {/* Tree-grid header */}
      <div className="rounded-md border border-slate-200 bg-white shadow-sm">
        <div className="max-h-[calc(100vh-22rem)] overflow-auto" data-testid="approvals-scroll">
        <div className="min-w-[1280px]">
        <div
          className="border-b border-slate-200 grid grid-cols-[32px_24px_minmax(220px,2fr)_80px_92px_110px_160px_120px_140px_115px] gap-2 px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-200 font-bold sticky top-0 z-30 rounded-t-md"
          style={{ background: "linear-gradient(180deg,#102844 0%,#0c1f37 100%)" }}
          data-testid="approvals-header"
        >
          <div>
            <Checkbox
              checked={allItemIds.length > 0 && selected.size === allItemIds.length}
              onCheckedChange={toggleAll}
              data-testid="select-all-checkbox"
            />
          </div>
          <div></div>
          <div>Vendor / Item</div>
          <div className="text-right">Items / Qty</div>
          <div className="text-right">Date</div>
          <div className="text-right">Total / Rate</div>
          <div className="text-right">Δ Rate · Impact · 30d avg</div>
          <div>Last purchase</div>
          <div>Last payment (bank)</div>
          <div className="text-right">Outstanding (Tally)</div>
        </div>

        {loading && (
          <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
        )}
        {!loading && groups.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-10">No pending purchases. Use "Load sample data" or upload PetPooja invoices.</div>
        )}
        {!loading && groups.length > 0 && visibleGroups.length === 0 && (
          <div className="text-center text-sm text-slate-300 py-10" data-testid="no-flagged-empty-state">
            No flagged vendors right now — all rate changes are within tolerance. <button className="underline text-cyan-300 ml-1" onClick={() => setOnlyFlagged(false)}>Show all</button>
          </div>
        )}

        {visibleGroups.map((g, idx) => {
          // Show a divider after the last flagged vendor
          const prev = idx > 0 ? visibleGroups[idx - 1] : null;
          const showFlaggedDivider = prev && prev.flagged && !g.flagged;
          const showFlaggedHeader = idx === 0 && g.flagged;
          const ids = g.items.map((i) => i.id);
          const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
          const partialSel = !allSel && ids.some((id) => selected.has(id));
          const isOpen = expanded.has(g.vendor);
          const variancePill = g.max_rate_variance_pct;
          // Group flag direction: count flagged items by rate direction
          const flaggedItems = g.items.filter((i) => i.flagged);
          const upCount = flaggedItems.filter((i) => (i.rate_variance_pct ?? 0) > 0).length;
          const downCount = flaggedItems.length - upCount;
          const direction = !g.flagged ? null : (upCount > 0 && downCount === 0) ? "up" : (downCount > 0 && upCount === 0) ? "down" : "mixed";
          const rowTint = direction === "up" ? "row-tint-up"
                        : direction === "down" ? "row-tint-down"
                        : direction === "mixed" ? "row-tint-mixed"
                        : "hover:bg-slate-50/60";
          const vendorBadgeCls = direction === "up"   ? "var-pill var-up-strong"
                              : direction === "down" ? "var-pill var-down-strong"
                              : direction === "mixed"? "var-pill var-neutral"
                              : "";
          const vendorBadgeLabel = direction === "up"   ? `${upCount} rate up`
                                 : direction === "down" ? `${downCount} rate down`
                                 : direction === "mixed" ? `${upCount}↑ ${downCount}↓`
                                 : "";
          return (
            <React.Fragment key={g.vendor}>
              {showFlaggedHeader && (
                <div
                  className="border-b border-rose-500/30 px-4 py-2 flex items-center gap-2"
                  style={{ background: "linear-gradient(90deg, rgba(239,68,68,0.20) 0%, rgba(239,68,68,0.06) 60%, transparent 100%)" }}
                  data-testid="flagged-section-header"
                >
                  <Warning size={14} weight="fill" className="text-rose-400" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-200">Needs review · price moved</span>
                  <span className="text-[10px] text-rose-300/80 ml-1">— review these first, then sign off the rest below</span>
                </div>
              )}
              {showFlaggedDivider && (
                <div
                  className="border-y border-slate-200 px-4 py-2 flex items-center gap-2"
                  style={{ background: "linear-gradient(180deg,#0e2540 0%,#0a1c30 100%)" }}
                  data-testid="unflagged-section-header"
                >
                  <Check size={14} weight="bold" className="text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-200">Rate stable · routine approvals</span>
                </div>
              )}
              <div className="border-b border-slate-100 last:border-b-0" data-testid={`vendor-group-${g.vendor}`}>
              {/* Vendor row */}
              <div
                className={`grid grid-cols-[32px_24px_minmax(220px,2fr)_80px_92px_110px_160px_120px_140px_115px] gap-2 px-3 py-3 items-center cursor-pointer transition-colors ${rowTint}`}
                onClick={() => toggleExpand(g.vendor)}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={allSel}
                    onCheckedChange={() => toggleVendor(g)}
                    className={partialSel ? "data-[state=unchecked]:bg-violet-300 data-[state=unchecked]:border-violet-300" : ""}
                    data-testid={`vendor-checkbox-${g.vendor}`}
                  />
                </div>
                <div>
                  <CaretRight
                    size={14}
                    weight="bold"
                    className={`transition-transform text-slate-500 ${isOpen ? "rotate-90" : ""}`}
                    data-testid={`vendor-toggle-${g.vendor}`}
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm truncate">{g.vendor}</span>
                    {g.flagged && (
                      <Badge className={`${vendorBadgeCls} rounded-sm text-[10px] uppercase tracking-wider`} data-testid={`vendor-flag-${g.vendor}`}>
                        <Warning size={11} weight="fill" className="mr-1" /> {vendorBadgeLabel}
                      </Badge>
                    )}
                    {g.stores.slice(0, 2).map((s) => (
                      <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded-sm border uppercase tracking-wider font-bold ${STORE_COLORS[s] || FALLBACK_STORE_COLOR}`}>
                        <Storefront size={9} weight="bold" className="inline mr-1" />{s}
                      </span>
                    ))}
                    {g.stores.length > 2 && (
                      <span className="text-[10px] text-slate-400 font-bold">+{g.stores.length - 2}</span>
                    )}
                  </div>
                </div>
                <div className="text-right text-sm text-slate-600 font-mono tabular-nums">
                  <span className="font-bold text-slate-900">{g.item_count}</span><span className="text-slate-400"> · {g.total_qty}</span>
                </div>
                <div className="text-right text-xs text-slate-500">{fmtDate(g.earliest_date)}</div>
                <div className="text-right">
                  <div className="font-mono tabular-nums text-sm font-bold text-slate-900">{fmtINR(g.total_amount)}</div>
                </div>
                <div className="text-right">
                  {variancePill !== null && variancePill !== undefined ? (
                    <div className="space-y-1">
                      <div className="flex justify-end">
                        <VariancePill value={variancePill} flagged={g.flagged} />
                      </div>
                      {(() => {
                        const totalImpact = g.items.reduce((s, it) => s + (it.total_impact ?? 0), 0);
                        if (!totalImpact) return null;
                        const tone = totalImpact > 0 ? "var-text-up" : "var-text-down";
                        return (
                          <div className={`text-[11px] font-mono tabular-nums font-bold ${tone}`} data-testid={`vendor-impact-${g.vendor}`}>
                            Impact {totalImpact > 0 ? "+" : ""}{fmtINR(totalImpact)}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">—</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 leading-tight" data-testid={`vendor-last-purchase-${g.vendor}`}>
                  {(() => {
                    const matched = g.items.filter((i) => i.last_amount);
                    if (!matched.length) return g.flagged ? "Compare ↓" : "—";
                    const lastTotal = matched.reduce((s, i) => s + (i.last_amount || 0), 0);
                    const lastDate = matched
                      .map((i) => i.last_date)
                      .filter(Boolean)
                      .sort()
                      .slice(-1)[0];
                    const stores = Array.from(new Set(matched.map((i) => i.last_store).filter(Boolean)));
                    return (
                      <div className="space-y-0.5">
                        <div className="font-mono tabular-nums font-bold text-slate-100">{fmtINR(lastTotal)}</div>
                        <div className="text-[10px] text-slate-400">
                          {fmtDate(lastDate)}
                          {stores.length === 1 && <span className="ml-1 text-cyan-300">· {stores[0]}</span>}
                          {stores.length > 1 && <span className="ml-1 text-cyan-300">· {stores.length} stores</span>}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {matched.length} of {g.items.length} matched
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Last Payment — matched from bank statement */}
                <div className="text-[11px] leading-tight" data-testid={`vendor-last-payment-${g.vendor}`}>
                  {g.last_payment_date ? (
                    <div className="space-y-0.5">
                      <div className="font-mono tabular-nums font-bold text-emerald-300">{fmtINR(g.last_payment_amount)}</div>
                      <div className="text-[10px] text-slate-400">
                        {fmtDate(g.last_payment_date)}
                        {g.last_payment_account && <span className="ml-1 text-cyan-300">· {g.last_payment_account}</span>}
                      </div>
                      <div
                        className="text-[10px] text-slate-500 truncate"
                        title={`${g.last_payment_description || ""}${g.last_payment_ref ? `\nUTR/Ref: ${g.last_payment_ref}` : ""}`}
                      >
                        {g.last_payment_ref ? `Ref ${g.last_payment_ref}` : (g.last_payment_description || "").slice(0, 22)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-500">No bank match (180d)</span>
                  )}
                </div>

                {/* Outstanding (Tally) */}
                <div className="text-right text-[11px] leading-tight" data-testid={`vendor-outstanding-${g.vendor}`}>
                  {g.tally_outstanding != null ? (
                    <div className="space-y-0.5">
                      <div className={`font-mono tabular-nums font-bold ${g.tally_outstanding > 0 ? "text-rose-300" : g.tally_outstanding < 0 ? "text-emerald-300" : "text-slate-300"}`}>
                        {fmtINR(Math.abs(g.tally_outstanding))}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {g.tally_outstanding > 0 ? "we owe" : g.tally_outstanding < 0 ? "advance with vendor" : "settled"} · {fmtDate(g.tally_as_of)}
                      </div>
                      {g.tally_party_matched && g.tally_party_matched.toLowerCase() !== g.vendor.toLowerCase() && (
                        <div
                          className="text-[10px] text-amber-300 truncate"
                          title={`Matched against Tally party: ${g.tally_party_matched}\n\nTally totals — Billed (credit) ₹${g.tally_credit_total?.toLocaleString("en-IN") || "—"} · Paid (debit) ₹${g.tally_debit_total?.toLocaleString("en-IN") || "—"}\nNet = ₹${g.tally_outstanding.toLocaleString("en-IN")}`}
                        >
                          ≈ {g.tally_party_matched}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-500">Not in Tally</span>
                  )}
                </div>
              </div>

              {/* Expanded children */}
              {isOpen && (
                <div className="bg-slate-50/40 border-t border-slate-100">
                  {g.items.map((it) => (
                    <ItemRow
                      key={it.id}
                      it={it}
                      checked={selected.has(it.id)}
                      onToggle={() => toggleItem(it.id)}
                    />
                  ))}
                </div>
              )}
              </div>
            </React.Fragment>
          );
        })}
        </div>
        </div>
      </div>
    </div>
  );
}

function VariancePill({ value, flagged }) {
  const up = value > 0;
  // Rate UP = red (cost concern). Rate DOWN = green (favourable).
  // Flagged variants are saturated/strong; unflagged variants are muted.
  const cls = up
    ? (flagged ? "var-pill var-up-strong" : "var-pill var-up")
    : (flagged ? "var-pill var-down-strong" : "var-pill var-down");
  return (
    <span className={cls}>
      {up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
      {fmtPct(value)}
    </span>
  );
}

function ItemRow({ it, checked, onToggle }) {
  const flagged = it.flagged;
  const rateUp = (it.rate_variance_pct ?? 0) > 0;
  // Row tint + badge palette follow flag direction (red = rate up, green = rate down)
  const rowTint = flagged ? (rateUp ? "row-tint-up" : "row-tint-down") : "";
  const badgeCls = flagged
    ? (rateUp ? "var-pill var-up-strong" : "var-pill var-down-strong")
    : "";
  return (
    <div
      className={`grid grid-cols-[32px_24px_minmax(220px,2fr)_80px_92px_110px_160px_120px_140px_115px] gap-2 px-3 py-2 items-center border-b border-slate-100 last:border-b-0 ${rowTint}`}
      data-testid={`approval-row-${it.id}`}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`row-checkbox-${it.id}`} />
      </div>
      <div />
      <div className="min-w-0 pl-4">
        <div className="text-sm text-slate-700 truncate flex items-center gap-2 flex-wrap">
          <span className="text-slate-300">└</span>
          {it.item}
          {(it.store_name || it.store) && (
            <span
              className="px-1.5 py-0.5 rounded-sm border bg-slate-50 border-slate-300 text-[10px] font-bold uppercase tracking-wider text-slate-700"
              data-testid={`store-${it.id}`}
              title="Outlet this PO was raised for"
            >
              {it.store_name || it.store}
            </span>
          )}
          {flagged && (
            <Badge className={`${badgeCls} rounded-sm font-bold text-[10px] uppercase tracking-wider`} data-testid={`flag-badge-${it.id}`} title={it.flag_reason || ""}>
              <Warning size={10} weight="fill" className="mr-1" /> {it.flag_reason || "Flagged"}
            </Badge>
          )}
        </div>
        {/* PO numbers — when flagged, show BOTH current and previous side by side */}
        {(it.po_number || it.last_po_number) && (
          <div className="text-[10px] font-mono ml-4 mt-1 flex items-center gap-2 flex-wrap">
            {it.po_number && (
              <span className={`px-1.5 py-0.5 rounded-sm border ${flagged ? "bg-white border-slate-300 text-slate-700 font-bold" : "text-slate-400 border-transparent"}`} title="Current PO">
                <span className="opacity-60 mr-1 uppercase tracking-wider">Now</span>{it.po_number}
              </span>
            )}
            {flagged && it.last_po_number && (
              <>
                <span className="text-slate-300">↔</span>
                <span className="px-1.5 py-0.5 rounded-sm border bg-slate-50 border-slate-300 text-slate-600" title="Previous PO used for comparison">
                  <span className="opacity-60 mr-1 uppercase tracking-wider">Prev</span>{it.last_po_number}
                </span>
              </>
            )}
            {it.created_by && (
              <span
                className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold"
                title="Who raised this PO in PetPooja"
                data-testid={`created-by-${it.id}`}
              >
                <span className="opacity-60 mr-1 uppercase tracking-wider">By</span>{it.created_by}
              </span>
            )}
          </div>
        )}
        {!it.po_number && !it.last_po_number && it.created_by && (
          <div className="text-[10px] font-mono ml-4 mt-1">
            <span
              className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold"
              title="Who raised this PO in PetPooja"
              data-testid={`created-by-${it.id}`}
            >
              <span className="opacity-60 mr-1 uppercase tracking-wider">By</span>{it.created_by}
            </span>
          </div>
        )}
      </div>
      <div className="text-right text-sm font-mono tabular-nums text-slate-700">{it.qty}{it.unit ? ` ${it.unit}` : ""}</div>
      <div className="text-right text-xs text-slate-500">{fmtDate(it.invoice_date)}</div>
      <div className="text-right">
        <div className="font-mono tabular-nums text-sm font-semibold text-slate-900">{fmtINR(it.amount)}</div>
        <div className="text-[10px] font-mono tabular-nums text-slate-500">@ {fmtINR(it.rate)}</div>
      </div>
      <div className="text-right">
        {it.rate_variance_pct === null || it.rate_variance_pct === undefined ? (
          <div className="space-y-0.5">
            <span className="text-slate-400 text-sm">—</span>
            {it.rate_avg_30d != null && (
              <div className="text-[10px] font-mono tabular-nums text-slate-600 font-bold" data-testid={`avg30d-${it.id}`}>
                30d avg <span className="text-slate-900">{fmtINR(it.rate_avg_30d)}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex justify-end">
              <VariancePill value={it.rate_variance_pct} flagged={flagged} />
            </div>
            {(it.rate_delta_per_unit != null) && (
              <div
                className={`text-[11px] font-mono tabular-nums font-bold ${flagged ? (rateUp ? "var-text-up" : "var-text-down") : "text-slate-300"}`}
                data-testid={`rate-delta-${it.id}`}
              >
                {it.rate_delta_per_unit > 0 ? "+" : ""}
                {fmtINR(it.rate_delta_per_unit)}/unit
              </div>
            )}
            {(it.total_impact != null) && (
              <div
                className={`text-[11px] font-mono tabular-nums font-bold ${flagged ? (rateUp ? "var-text-up" : "var-text-down") : "text-slate-200"}`}
                data-testid={`total-impact-${it.id}`}
                title="Δ rate × qty"
              >
                Impact <span className="font-bold">{it.total_impact > 0 ? "+" : ""}{fmtINR(it.total_impact)}</span>
              </div>
            )}
            {it.rate_avg_30d != null && (
              <div
                className="text-[10px] font-mono tabular-nums text-slate-600 font-bold border-t border-slate-200/70 pt-0.5 mt-0.5"
                data-testid={`avg30d-${it.id}`}
                title="Mean rate of this item from same vendor over the last 30 days"
              >
                30d avg <span className="text-slate-900">{fmtINR(it.rate_avg_30d)}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-500">
        {it.last_amount ? (
          <div className="leading-tight" data-testid={`item-last-purchase-${it.id}`}>
            <div className="flex items-baseline gap-1">
              <span className="font-mono tabular-nums font-bold text-slate-100">{fmtINR(it.last_amount)}</span>
              <span className="text-[10px] text-slate-400">total</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
              {fmtINR(it.last_rate)}/u · qty {it.last_qty ?? "—"}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              <span
                className={`px-1 py-0.5 rounded-sm font-bold uppercase tracking-wider ${
                  it.last_store && it.store && (it.last_store || "").toLowerCase().replace(/\W/g, "") !== (it.store || "").toLowerCase().replace(/\W/g, "")
                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                    : "bg-slate-100 text-slate-600"
                }`}
                title={
                  it.last_store && it.store && it.last_store.toLowerCase() !== it.store.toLowerCase()
                    ? "Compared against a PO from a different outlet"
                    : "Same outlet as current PO"
                }
                data-testid={`last-store-${it.id}`}
              >
                {it.last_store}
              </span>
              <span className="ml-1 text-slate-400">· {fmtDate(it.last_date)}</span>
              {it.last_po_number && (
                <span className="ml-1 text-slate-400">· PO #{it.last_po_number}</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">First purchase</span>
        )}
      </div>
      {/* Last Payment cell — kept empty per item; vendor-level row shows the bank match */}
      <div></div>
      {/* Outstanding cell — vendor-level only */}
      <div></div>
    </div>
  );
}

function Chip({ label, value, color, onClick, testId }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`px-2.5 py-1 rounded-sm border text-xs flex items-center gap-2 ${color} ${clickable ? "select-none" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      data-testid={testId}
    >
      <span className="opacity-70 text-[10px] uppercase tracking-wider font-bold">{label}</span>
      <span className="font-mono tabular-nums font-bold">{value}</span>
    </div>
  );
}

// ============================================================================
// Tab strip wrapper: Reconciliation Approvals (existing) ↔ New PO Approvals
// ============================================================================

const NEW_PO_STATUS_STYLES = {
  pending:  "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

function pdfUrlFor(po) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/new-pos/${po.id}/pdf`;
}

function NewPOApprovalsContent({ refreshKey }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("pending");
  const [filterText, setFilterText]     = useState("");
  const [sortBy, setSortBy]   = useState("uploaded_at");
  const [sortDir, setSortDir] = useState("desc");
  // Expanded rows: id -> { loading, data }
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const r = await api.get("/new-pos", { params });
      setItems(r.data || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load New PO approvals");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Auto-expand the first PO when the list loads so users immediately see the
  // item-level analytics (rate impact, 30d avg, last purchase, etc.).
  useEffect(() => {
    if (items.length > 0 && Object.keys(expanded).length === 0) {
      toggleExpand(items[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Refresh when user returns to the tab (e.g., after uploading in Data → PO).
  useEffect(() => {
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const pendingIds = items.filter((p) => p.status === "pending").map((p) => p.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pendingIds));
  const toggleOne = (id) => {
    // Guard: never let approved/rejected rows into the selection set.
    const po = items.find((p) => p.id === id);
    if (po && po.status !== "pending") return;
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const toggleExpand = async (id) => {
    if (expanded[id]) {
      const n = { ...expanded };
      delete n[id];
      setExpanded(n);
      return;
    }
    // Open + lazy-load
    setExpanded((e) => ({ ...e, [id]: { loading: true, data: null } }));
    try {
      const r = await api.get(`/new-pos/${id}/enriched-lines`);
      setExpanded((e) => ({ ...e, [id]: { loading: false, data: r.data } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load line analytics");
      setExpanded((prev) => {
        const n = { ...prev }; delete n[id]; return n;
      });
    }
  };

  // Per-PO comment input — keyed by po.id so each row keeps its own buffer.
  const [comments, setComments] = useState({});
  const setComment = (id, val) => setComments((m) => ({ ...m, [id]: val }));
  const [modes, setModes] = useState({});
  const [modePrompt, setModePrompt] = useState(null);

  const action = async (act, extraModes) => {
    if (selected.size === 0) { toast.error("Select at least one PO"); return; }
    // Send a comments map for only the selected POs that have text typed.
    const ids = Array.from(selected);
    const cmap = {};
    ids.forEach((id) => {
      const v = (comments[id] || "").trim();
      if (v) cmap[id] = v;
    });
    const atL1 = (po) => Number(po.current_level || 1) <= 1;
    const allModes = { ...modes, ...(extraModes || {}) };
    const missing = items.filter((p) => selected.has(p.id) && atL1(p) && !(allModes[p.id] || p.mode_of_payment));
    if (act === "approve" && missing.length) {
      setModePrompt({ pos: missing, picks: {} });
      return;
    }
    const mmap = {};
    ids.forEach((id) => { if (allModes[id]) mmap[id] = allModes[id]; });
    try {
      const r = await api.post("/new-pos/action", {
        ids,
        action: act,
        comments: Object.keys(cmap).length ? cmap : undefined,
        modes: Object.keys(mmap).length ? mmap : undefined,
      });
      (r.data?.blocked || []).forEach((b) => toast.error(`${b.po_number || b.vendor}: ${b.message}`));
      toast.success(`${r.data.updated} PO(s) ${act === "approve" ? "approved" : "rejected"}`);
      // Clear per-row comments for the acted-on POs.
      setComments((m) => {
        const next = { ...m };
        ids.forEach((id) => { delete next[id]; });
        return next;
      });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const totalAmount = items.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  // Apply text filter + column sort on top of the loaded items.
  const filteredSorted = React.useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let rows = items;
    if (q) {
      rows = rows.filter((p) => {
        const blob = [
          p.vendor, p.po_number, p.outlet, p.uploaded_by, p.status,
          p.po_date, p.amount,
        ].map((v) => (v == null ? "" : String(v))).join(" ").toLowerCase();
        return blob.includes(q);
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const fieldFor = (p) => {
      switch (sortBy) {
        case "vendor":      return (p.vendor || "").toLowerCase();
        case "po_number":   return (p.po_number || "").toLowerCase();
        case "po_date":     return p.po_date || "";
        case "amount":      return Number(p.amount) || 0;
        case "outlet":      return (p.outlet || "").toLowerCase();
        case "uploaded_by": return (p.uploaded_by || "").toLowerCase();
        case "status":      return (p.status || "").toLowerCase();
        case "uploaded_at": return p.uploaded_at || "";
        default:            return "";
      }
    };
    return [...rows].sort((a, b) => {
      const va = fieldFor(a), vb = fieldFor(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [items, filterText, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir(col === "vendor" || col === "po_number" ? "asc" : "desc"); }
  };
  const sortArrow = (col) =>
    sortBy !== col ? null : (sortDir === "asc" ? " ▲" : " ▼");

  const downloadXlsx = async () => {
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const r = await api.get("/new-pos/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `new-pos-${statusFilter || "all"}-${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div data-testid="new-po-approvals-content" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-rose-100 text-rose-700"><FilePdf size={15} weight="duotone" /></div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">New PO approvals</h2>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Chip label="POs" value={filteredSorted.length} color="bg-slate-100 text-slate-700 border-slate-200" />
            <Chip label="Total value" value={fmtINR(filteredSorted.reduce((s, p) => s + (Number(p.amount) || 0), 0))} color="bg-indigo-100 text-indigo-700 border-indigo-200" />
            <StatusPills value={statusFilter} onChange={setStatusFilter} />
            <div className="relative">
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filter vendor / PO# / outlet…"
                className="h-8 pl-2.5 pr-7 rounded-sm border border-slate-200 bg-white text-[12px] focus:outline-none focus:ring-1 focus:ring-cyan-300 w-[260px]"
                data-testid="new-po-text-filter"
              />
              {filterText && (
                <button
                  onClick={() => setFilterText("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="new-po-text-filter-clear"
                  title="Clear"
                ><X size={11} weight="bold" /></button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ModePromptDialog prompt={modePrompt} setPrompt={setModePrompt}
            onConfirm={() => { const picks = modePrompt.picks; setModes((m) => ({ ...m, ...picks })); setModePrompt(null); action("approve", picks); }} />
          <Button variant="outline" size="sm" onClick={load}
            className="h-9 rounded-sm border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="new-po-refresh-btn"
            title="Reload latest POs">
            <ArrowsClockwise size={14} weight="bold" />
          </Button>
          <Button variant="outline" size="sm" onClick={downloadXlsx}
            className="h-9 rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            data-testid="new-po-download-xlsx-btn"
            title={`Download all ${statusFilter === "all" ? "" : statusFilter + " "}POs as XLSX`}>
            <DownloadSimple size={14} weight="bold" className="mr-1.5" /> XLSX
          </Button>
          {/* Hide bulk Approve/Reject entirely when the current view has no
              actionable (pending) rows — avoids a "why is Approve highlighted
              if I can't click it?" moment for the user. */}
          {pendingIds.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => action("reject")}
                className="h-9 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                data-testid="new-po-reject-btn"
                disabled={selected.size === 0}>
                <X size={14} weight="bold" className="mr-1.5" /> Reject {selected.size > 0 && `(${selected.size})`}
              </Button>
              <Button size="sm" onClick={() => action("approve")}
                className="h-9 rounded-sm bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-sm disabled:from-slate-300 disabled:to-slate-400 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none"
                data-testid="new-po-approve-btn"
                disabled={selected.size === 0}>
                <Check size={14} weight="bold" className="mr-1.5" /> Approve {selected.size > 0 && `(${selected.size})`}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white shadow-sm">
        <div className="px-3 py-2 bg-cyan-50 border-b border-cyan-200 text-[11px] text-cyan-800 flex items-center gap-2" data-testid="new-po-expand-hint">
          <CaretRight size={12} weight="bold" className="text-cyan-600" />
          <span>
            Click the <span className="font-bold">▶</span> caret on any row to see <span className="font-bold">item analytics</span> —
            qty, last purchase, 30-day avg price, rate impact %, and vendor's total outstanding.
          </span>
        </div>
        <div className="max-h-[calc(100vh-22rem)] overflow-auto" data-testid="new-po-approvals-scroll">
          <div className="min-w-[1100px]">
            <div
              className="border-b border-slate-200 grid grid-cols-[24px_32px_80px_minmax(180px,2fr)_140px_110px_120px_minmax(160px,1.6fr)_140px_minmax(190px,1.2fr)] gap-2 px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-200 font-bold sticky top-0 z-30 rounded-t-md"
              style={{ background: "linear-gradient(180deg,#102844 0%,#0c1f37 100%)" }}
              data-testid="new-po-approvals-header"
            >
              <div />
              <div>
                {pendingIds.length > 0 ? (
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="new-po-select-all" />
                ) : null}
              </div>
              <div>PDF</div>
              <div onClick={() => toggleSort("vendor")}      className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-vendor">Vendor{sortArrow("vendor")}</div>
              <div onClick={() => toggleSort("po_number")}   className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-po_number">PO #{sortArrow("po_number")}</div>
              <div onClick={() => toggleSort("po_date")}     className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-po_date">Date{sortArrow("po_date")}</div>
              <div onClick={() => toggleSort("amount")}      className="cursor-pointer select-none hover:text-cyan-200 text-right" data-testid="new-po-sort-amount">Amount{sortArrow("amount")}</div>
              <div onClick={() => toggleSort("outlet")}      className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-outlet">Outlet{sortArrow("outlet")}</div>
              <div onClick={() => toggleSort("uploaded_by")} className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-uploaded_by">Uploaded By{sortArrow("uploaded_by")}</div>
              <div onClick={() => toggleSort("status")}      className="cursor-pointer select-none hover:text-cyan-200" data-testid="new-po-sort-status">Status &amp; Chain{sortArrow("status")}</div>
            </div>

            {loading && <div className="text-center text-sm text-slate-500 py-10">Loading…</div>}
            {!loading && filteredSorted.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-10" data-testid="new-po-approvals-empty">
                {items.length > 0 && filterText
                  ? <>No POs match <span className="font-semibold">"{filterText}"</span>.</>
                  : (
                    <>No {statusFilter !== "all" ? statusFilter : ""} POs.
                      {statusFilter === "pending" && (
                        <span> Upload some in <span className="text-cyan-700 font-semibold">Data → PO → Upload New PO</span>.</span>
                      )}
                    </>
                  )}
              </div>
            )}

            {filteredSorted.map((po) => {
              const isSel = selected.has(po.id);
              const exp = expanded[po.id];
              const isOpen = !!exp;
              const isPending = po.status === "pending";
              return (
                <React.Fragment key={po.id}>
                  <div
                    className={
                      "border-b border-slate-100 grid grid-cols-[24px_32px_80px_minmax(180px,2fr)_140px_110px_120px_minmax(160px,1.6fr)_140px_minmax(190px,1.2fr)] gap-2 px-3 py-2.5 text-sm items-center " +
                      (isSel ? "bg-cyan-50/60" : "hover:bg-slate-50/60")
                    }
                    data-testid={`new-po-row-${po.id}`}
                  >
                    <button
                      onClick={() => toggleExpand(po.id)}
                      className={
                        "h-6 w-6 grid place-items-center rounded-sm transition-colors " +
                        (isOpen
                          ? "bg-cyan-600 text-white"
                          : "bg-slate-100 text-cyan-700 hover:bg-cyan-100 ring-1 ring-cyan-200")
                      }
                      data-testid={`new-po-expand-${po.id}`}
                      title={isOpen ? "Collapse" : "Show item analytics (rate impact, last purchase, 30d avg…)"}
                    >
                      <CaretRight size={14} weight="bold" className={"transition-transform " + (isOpen ? "rotate-90" : "")} />
                    </button>
                    <div>
                      {po.status === "pending" ? (
                        <Checkbox checked={isSel} onCheckedChange={() => toggleOne(po.id)} data-testid={`new-po-select-${po.id}`} />
                      ) : (
                        <span className="inline-block h-4 w-4" />
                      )}
                    </div>
                    <div>
                      <a
                        href={pdfUrlFor(po)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 hover:text-rose-800 underline"
                        data-testid={`new-po-pdf-link-${po.id}`}
                      >
                        <FilePdf size={12} weight="duotone" /> PDF
                      </a>
                    </div>
                    <div className="font-semibold text-slate-800 truncate" title={po.vendor || "(missing)"}>
                      {po.vendor || <span className="italic text-slate-400">(missing)</span>}
                    </div>
                    <div className="font-mono text-xs text-amber-700">{po.po_number || "—"}</div>
                    <div className="font-mono text-xs text-slate-600">{fmtDate(po.po_date) || "—"}</div>
                    <div className="text-right font-mono tabular-nums font-bold text-slate-900">
                      {po.amount != null ? fmtINR(po.amount) : "—"}
                    </div>
                    <div className="text-xs text-slate-600 truncate" title={po.outlet || ""}>{po.outlet || "—"}</div>
                    <div className="text-xs text-slate-500 truncate" title={po.uploaded_by || ""}>{po.uploaded_by || "—"}</div>
                    <div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${NEW_PO_STATUS_STYLES[po.status] || NEW_PO_STATUS_STYLES.pending}`}>
                        {po.status}
                      </span>
                      <ApprovalChainStrip po={po} />
                      {isPending && Number(po.current_level || 1) <= 1 ? (
                        <POModeSelect value={modes[po.id] || po.mode_of_payment || ""}
                          onChange={(m) => setModes((x) => ({ ...x, [po.id]: m }))} testId={`new-po-mode-${po.id}`} />
                      ) : po.mode_of_payment ? (
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-cyan-700" data-testid={`new-po-mode-badge-${po.id}`}>
                          Mode: {po.mode_of_payment}
                        </div>
                      ) : null}
                      {isPending && (
                        <input
                          type="text"
                          value={comments[po.id] || ""}
                          onChange={(e) => setComment(po.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Comment (optional)…"
                          className="mt-1.5 w-full h-7 px-2 rounded-sm border border-slate-200 bg-white text-[11px] focus:outline-none focus:ring-1 focus:ring-cyan-400 placeholder:text-slate-400"
                          data-testid={`new-po-comment-${po.id}`}
                          maxLength={300}
                        />
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <NewPOLinesPanel poId={po.id} state={exp} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ApprovalChainStrip({ po }) {
  // Backend record shape:
  //   approvals: [{level, role_id, role_label, approved_by, approved_at}]
  //              (a 'rejected' entry uses rejected_by/rejected_at)
  //   actioned_by / actioned_at: top-level final action.
  //   total_levels / current_level / pending_role_label for in-flight chain.
  const taken = Array.isArray(po.approvals) ? po.approvals : [];
  // Render strictly the configured number of levels — never let an audit-trail
  // overflow blow the chain strip past total_levels (which used to produce
  // L1..L7 after a reject↔re-approve round trip).
  const totalLevels = po.total_levels || 0;

  // Build pills only for configured levels.
  const steps = [];
  for (let lvl = 1; lvl <= totalLevels; lvl++) {
    // Pick the LATEST entry recorded for this level so legacy POs that stored
    // multiple cycles in approvals[] still render the current state.
    let t = null;
    for (let i = taken.length - 1; i >= 0; i--) {
      if ((taken[i].level || 0) === lvl) { t = taken[i]; break; }
    }
    const action = t
      ? (t.rejected_by ? "reject" : t.approved_by ? "approve" : null)
      : null;
    steps.push({
      level: lvl,
      role:  t?.role_label || t?.role_id || `L${lvl}`,
      action,
      at:    t?.approved_at || t?.rejected_at || null,
      by:    t?.approved_by || t?.rejected_by || null,
      comment: t?.comment || t?.reason || null,
    });
  }

  const finalAt = po.actioned_at || (taken.length ? (taken[taken.length - 1].approved_at || taken[taken.length - 1].rejected_at) : null);
  const finalBy = po.actioned_by || (taken.length ? (taken[taken.length - 1].approved_by || taken[taken.length - 1].rejected_by) : null);
  const isFinal = po.status === "approved" || po.status === "rejected";

  if (steps.length === 0) {
    if (isFinal && finalAt) {
      return (
        <div className="mt-1 text-[9px] uppercase tracking-wider font-bold text-slate-500" data-testid={`new-po-final-date-${po.id}`}>
          {po.status === "approved" ? "Approved" : "Rejected"} · {new Date(finalAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mt-1.5 space-y-1" data-testid={`new-po-chain-${po.id}`}>
      <div className="flex items-center gap-0.5 flex-wrap">
        {steps.map((s, i) => {
          const tone = s.action === "approve"
            ? "bg-emerald-100 text-emerald-700 border-emerald-300"
            : s.action === "reject"
              ? "bg-rose-100 text-rose-700 border-rose-300"
              : (po.status === "pending" && (po.current_level || 1) === s.level)
                ? "bg-cyan-100 text-cyan-700 border-cyan-300 ring-1 ring-cyan-200"
                : "bg-slate-100 text-slate-500 border-slate-200";
          const icon = s.action === "approve" ? "✓"
                     : s.action === "reject"  ? "✕"
                     : (po.status === "pending" && (po.current_level || 1) === s.level) ? "•"
                     : "·";
          const tip = `L${s.level} · ${s.role}${s.action ? ` · ${s.action}` : ""}${s.at ? ` on ${new Date(s.at).toLocaleDateString("en-IN")}` : ""}${s.by ? ` by ${s.by}` : ""}${s.comment ? `\n💬 ${s.comment}` : ""}`;
          return (
            <React.Fragment key={s.level}>
              <span
                className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm border text-[9px] font-bold uppercase tracking-wider ${tone}`}
                title={tip}
              >
                <span>L{s.level}</span>
                <span>{icon}</span>
              </span>
              {i < steps.length - 1 && <span className="text-slate-400 text-[9px]">→</span>}
            </React.Fragment>
          );
        })}
      </div>
      {isFinal && finalAt && (
        <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500" data-testid={`new-po-final-date-${po.id}`}>
          Final {po.status === "approved" ? "✓" : "✕"} {new Date(finalAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
          {finalBy && <span className="text-slate-400 normal-case tracking-normal"> · {String(finalBy).split("@")[0]}</span>}
        </div>
      )}
      {!isFinal && po.pending_role_label && (
        <div className="text-[9px] uppercase tracking-wider font-bold text-cyan-700">
          Awaiting · {po.pending_role_label}
        </div>
      )}
      {/* Surface any comments left by approvers on this PO so the audit
          trail is visible inline (not just in the hover tooltip). */}
      {steps.some((s) => s.comment) && (
        <div className="mt-1 space-y-0.5" data-testid={`new-po-chain-comments-${po.id}`}>
          {steps.filter((s) => s.comment).map((s) => (
            <div key={`c-${s.level}`} className="text-[10px] text-slate-600 leading-snug">
              <span className={`mr-1 font-bold ${s.action === "reject" ? "text-rose-600" : "text-emerald-700"}`}>
                L{s.level} {s.action === "reject" ? "✕" : "✓"}
              </span>
              <span className="text-slate-500">"{s.comment}"</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPills({ value, onChange }) {
  const opts = [
    { value: "pending",  label: "Pending"  },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "all",      label: "All"      },
  ];
  return (
    <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid="new-po-status-filter">
      {opts.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={"px-2.5 h-8 text-[11px] font-semibold " + (active ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-100")}
            data-testid={`new-po-status-filter-${o.value}`}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

export default function ApprovalsTab({ store, refreshKey }) {
  // The Approvals tab is split into two sub-tabs: (1) PO Approvals — the
  // existing "New PO" chain flow, and (2) Payment Approvals — the new
  // Payment Request form + chain flow.
  void store; // kept for prop compatibility
  const [subTab, setSubTab] = useState(() => {
    try { return window.localStorage.getItem("approvals-sub-tab") || "po"; } catch { return "po"; }
  });
  const changeSub = (id) => {
    setSubTab(id);
    try { window.localStorage.setItem("approvals-sub-tab", id); } catch { /* ignore */ }
  };
  return (
    <div data-testid="approvals-tab-wrapper" className="space-y-4">
      <div className="inline-flex rounded-md border border-slate-800 bg-slate-950/60 p-0.5 gap-0.5" data-testid="approvals-sub-tabs">
        <button
          type="button"
          onClick={() => changeSub("po")}
          className={`h-8 px-3 rounded text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
            subTab === "po"
              ? "bg-cyan-950/80 text-cyan-100 border border-cyan-800"
              : "text-slate-400 hover:text-cyan-200"
          }`}
          data-testid="approvals-sub-tab-po"
        >
          <Receipt size={12} weight="duotone" />
          PO Approvals
        </button>
        <button
          type="button"
          onClick={() => changeSub("payment")}
          className={`h-8 px-3 rounded text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
            subTab === "payment"
              ? "bg-cyan-950/80 text-cyan-100 border border-cyan-800"
              : "text-slate-400 hover:text-cyan-200"
          }`}
          data-testid="approvals-sub-tab-payment"
        >
          <CurrencyInr size={12} weight="duotone" />
          Payment Approvals
        </button>
      </div>
      {subTab === "po" && <NewPOApprovalsContent refreshKey={refreshKey} />}
      {subTab === "payment" && <PaymentApprovalsContent refreshKey={refreshKey} />}
    </div>
  );
}

export function NewPOComparison({ poId, refreshKey }) {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, data: null });
    api.get(`/new-pos/${poId}/enriched-lines`)
      .then((r) => live && setState({ loading: false, data: r.data }))
      .catch(() => live && setState({ loading: false, data: null }));
    return () => { live = false; };
  }, [poId, refreshKey]);
  return <NewPOLinesPanel poId={poId} state={state} />;
}

function NewPOLinesPanel({ poId, state }) {
  if (state.loading) {
    return (
      <div className="border-b border-slate-100 px-4 py-3 bg-slate-50/80 text-xs text-slate-500" data-testid={`new-po-lines-loading-${poId}`}>
        Loading line-item analytics…
      </div>
    );
  }
  const d = state.data;
  if (!d) return null;

  return (
    <div className="border-b border-slate-200 px-4 py-3 bg-slate-900/95" data-testid={`new-po-lines-${poId}`}>
      {/* Vendor-level chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {d.total_outstanding != null && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-amber-500/60 bg-amber-900/30 shadow-sm"
            data-testid="new-po-chip-total-outstanding"
            title={d.matched_vendor && d.matched_vendor !== d.vendor ? `Matched as: ${d.matched_vendor}` : ""}>
            <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300">Total outstanding</span>
            <span className="font-mono tabular-nums font-bold text-amber-100 text-sm">{fmtINR(d.total_outstanding)}</span>
            {d.matched_vendor && d.matched_vendor.toLowerCase() !== (d.vendor || "").toLowerCase() && (
              <span className="text-[9px] text-amber-300/70 italic">≈ {d.matched_vendor}</span>
            )}
          </div>
        )}
        {d.total_outstanding == null && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-slate-500/40 bg-slate-800/40"
            data-testid="new-po-chip-total-outstanding">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total outstanding</span>
            <span className="font-mono text-slate-500 text-sm">— vendor not in Tally</span>
          </div>
        )}
        {d.last_payment ? (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-indigo-400/60 bg-indigo-900/30 shadow-sm"
            data-testid="new-po-chip-last-payment">
            <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-300">
              Last payment · {d.last_payment.days_ago != null ? `${d.last_payment.days_ago}d ago` : d.last_payment.date}
            </span>
            <span className="font-mono tabular-nums font-bold text-indigo-100 text-sm">{fmtINR(d.last_payment.amount)}</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-slate-500/40 bg-slate-800/40"
            data-testid="new-po-chip-last-payment">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Last payment</span>
            <span className="font-mono text-slate-500 text-sm">—</span>
          </div>
        )}
      </div>

      {/* Line items table */}
      <div className="rounded-sm border border-slate-700 bg-slate-950 overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]" data-testid={`new-po-lines-table-${poId}`}>
          <thead>
            <tr className="bg-slate-800 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-right px-2 py-2">Qty</th>
              <th className="text-right px-2 py-2">PO Rate</th>
              <th className="text-right px-2 py-2">Last Purchase<br /><span className="font-normal normal-case tracking-normal text-[9px] text-slate-400">rate · qty</span></th>
              <th className="text-right px-2 py-2">30d Avg</th>
              <th className="text-right px-2 py-2">Impact<br /><span className="font-normal normal-case tracking-normal text-[9px] text-slate-400">rate · qty</span></th>
              <th className="text-left px-3 py-2">Other Vendors<br /><span className="font-normal normal-case tracking-normal text-[9px] text-slate-400">same item · latest rate</span></th>
              <th className="text-right px-2 py-2">PO Amount</th>
            </tr>
          </thead>
          <tbody>
            {(d.line_items || []).length === 0 && (
              <tr><td colSpan={8} className="text-center py-4 text-slate-500">No line items extracted.</td></tr>
            )}
            {(d.line_items || []).map((li, i) => (
              <tr key={i} className="border-t border-slate-800 hover:bg-slate-900">
                <td className="px-3 py-2 font-semibold text-slate-100 align-top">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span title={li.product}>{li.product || "—"}</span>
                    {li.is_first_purchase && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-fuchsia-900/40 text-fuchsia-300 border border-fuchsia-700/60"
                        data-testid={`new-po-first-badge-${i}`}>
                        {li.best_other ? "First from this vendor" : "First purchase"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-300 align-top">
                  {li.qty ?? "—"}
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-200 align-top">
                  {li.rate != null ? fmtINR(li.rate) : "—"}
                </td>
                <td className="px-2 py-2 text-right align-top">
                  {li.last_purchase ? (
                    <div className="leading-tight">
                      <div className="font-mono tabular-nums text-slate-200">{fmtINR(li.last_purchase.rate)}</div>
                      <div className="text-[10px] text-slate-400 font-mono tabular-nums">
                        Qty {li.last_purchase.qty != null ? li.last_purchase.qty : "—"}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {li.last_purchase.days_ago != null ? `${li.last_purchase.days_ago}d ago` : li.last_purchase.date}
                      </div>
                      {li.last_purchase.other_vendor && (
                        <div className="text-[9.5px] text-fuchsia-300 truncate max-w-[120px] ml-auto" title={`Bought from ${li.last_purchase.vendor} (${li.last_purchase.source}${li.last_purchase.po_number ? ` ${li.last_purchase.po_number}` : ""})`}
                          data-testid={`new-po-last-other-${i}`}>
                          from {li.last_purchase.vendor}
                        </div>
                      )}
                    </div>
                  ) : <span className="text-slate-500">—</span>}
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums align-top">
                  {li.avg_30d_rate != null ? (
                    <span className="text-slate-200" title={li.avg_30d_basis === "all_vendors" ? "30-day average across all vendors" : "30-day average with this vendor"}>
                      {fmtINR(li.avg_30d_rate)}
                      {li.avg_30d_basis === "all_vendors" && <div className="text-[9px] text-fuchsia-300 font-sans">all vendors</div>}
                    </span>
                  ) : <span className="text-slate-500">—</span>}
                </td>
                <td className="px-2 py-2 text-right align-top">
                  <div className="flex flex-col items-end gap-1">
                    <RateImpactBadge impact={li.rate_impact} />
                    <QtyImpactBadge impact={li.qty_impact} />
                  </div>
                </td>
                <td className="px-3 py-2 align-top min-w-[220px]" data-testid={`new-po-other-vendors-${i}`}>
                  <OtherVendorsCell li={li} />
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-cyan-100 align-top">
                  {li.amount != null ? fmtINR(li.amount) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function POModeSelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()}
      className={`mt-1.5 w-full h-7 px-2 rounded-sm border text-[11px] focus:outline-none focus:ring-1 focus:ring-cyan-400 ${value ? "border-cyan-400 text-slate-800 bg-white" : "border-amber-300 text-amber-700 bg-amber-50"}`}
      data-testid={testId}>
      <option value="">Select mode</option>
      <option value="Cash">Cash</option>
      <option value="Bank">Bank</option>
    </select>
  );
}

function ModePromptDialog({ prompt, setPrompt, onConfirm }) {
  if (!prompt) return null;
  const ready = prompt.pos.every((p) => prompt.picks[p.id]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) setPrompt(null); }}>
      <DialogContent className="max-w-md" data-testid="po-mode-prompt">
        <DialogHeader>
          <DialogTitle>Select Mode of Payment</DialogTitle>
          <DialogDescription>L1 approval needs a mode for {prompt.pos.length === 1 ? "this PO" : `these ${prompt.pos.length} POs`}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {prompt.pos.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{p.vendor}</div>
                <div className="text-[11px] font-mono text-slate-500">{p.po_number || "—"} · {p.amount != null ? fmtINR(p.amount) : "—"}</div>
              </div>
              <div className="w-32">
                <POModeSelect value={prompt.picks[p.id] || ""} testId={`po-mode-prompt-${p.id}`}
                  onChange={(m) => setPrompt({ ...prompt, picks: { ...prompt.picks, [p.id]: m } })} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setPrompt(null)} data-testid="po-mode-prompt-cancel">Cancel</Button>
          <Button size="sm" disabled={!ready} onClick={onConfirm}
            className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="po-mode-prompt-confirm">
            <Check size={14} weight="bold" className="mr-1" />Approve
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OtherVendorsCell({ li }) {
  const list = li.other_vendors || [];
  if (!list.length) return <span className="text-slate-500 text-[11px]">No other vendor history</span>;
  return (
    <div className="space-y-1">
      {list.slice(0, 3).map((o, k) => {
        const tone = o.diff_pct == null || Math.abs(o.diff_pct) < 0.5 ? "text-slate-400"
          : o.diff_pct > 0 ? "text-rose-300" : "text-emerald-300";
        return (
          <div key={k} className="leading-tight" title={`${o.item} · ${o.source}${o.po_number ? ` ${o.po_number}` : ""}${o.store ? ` · ${o.store}` : ""} · ${o.date || ""}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-200 text-[11px] truncate max-w-[130px]">{o.vendor}</span>
              <span className="font-mono tabular-nums text-slate-100 text-[11px]">{fmtINR(o.rate)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-[9.5px]">
              <span className="text-slate-500">{o.date || "—"} · {o.source}</span>
              {o.diff_pct != null && (
                <span className={`font-mono font-bold ${tone}`}>
                  PO {o.diff_pct > 0 ? "▲" : o.diff_pct < 0 ? "▼" : "·"}{Math.abs(o.diff_pct).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        );
      })}
      {list.length > 3 && <div className="text-[9.5px] text-slate-500">+{list.length - 3} more vendor(s)</div>}
      {li.best_other_saving > 0 && (
        <div className="text-[10px] font-bold text-amber-300" data-testid="new-po-best-other-saving">
          Could save {fmtINR(li.best_other_saving)} at {li.best_other.vendor}
        </div>
      )}
    </div>
  );
}

function QtyImpactBadge({ impact }) {
  if (!impact || impact.pct == null) return null;
  const same = impact.direction === "same";
  const up   = impact.direction === "up";
  const tone = same
    ? "bg-slate-800 text-slate-300 border-slate-600"
    : up
      ? "bg-cyan-900/40 text-cyan-200 border-cyan-700/60"
      : "bg-amber-900/40 text-amber-200 border-amber-700/60";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider font-bold ${tone}`}
      title={`Qty ${impact.direction} vs last purchase qty ${impact.ref_qty}`}
    >
      Qty {up ? "▲" : same ? "·" : "▼"}
      {Math.abs(impact.pct).toFixed(0)}%
    </span>
  );
}

function RateImpactBadge({ impact }) {
  if (!impact) return <span className="text-slate-500">—</span>;
  if (impact.direction === "new") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-fuchsia-900/40 text-fuchsia-300 border border-fuchsia-700/60 text-[10px] uppercase tracking-wider font-bold">
        New
      </span>
    );
  }
  if (impact.direction === "same") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-slate-800 text-slate-400 border border-slate-700 text-[10px] uppercase tracking-wider font-bold">
        Same
      </span>
    );
  }
  const isDown = impact.direction === "down";
  return (
    <span
      className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold " +
        (isDown
          ? "bg-emerald-900/40 text-emerald-300 border border-emerald-600/60"
          : "bg-rose-900/40 text-rose-300 border border-rose-600/60")}
      title={
        impact.basis === "other_vendor"
          ? `vs ${impact.ref_vendor} at ${fmtINR(impact.ref_rate)} (cheapest other vendor)`
          : impact.basis === "amount_vs_unit"
          ? `vs last per-unit price ${impact.ref_rate ? fmtINR(impact.ref_rate) : ''} (line-amount comparison — qty differs from history)`
          : `vs 30-day avg ${impact.ref_rate ? fmtINR(impact.ref_rate) : ''}`
      }
    >
      {isDown ? <TrendDown size={10} weight="bold" /> : <TrendUp size={10} weight="bold" />}
      {Math.abs(impact.pct).toFixed(1)}%
    </span>
  );
}

function ViewTab({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 px-3 h-8 rounded-sm text-xs font-semibold transition-colors " +
        (active
          ? "bg-cyan-600 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100")
      }
      data-testid={testid}
    >
      {children}
    </button>
  );
}
